import React from "react";
import {
    FaFont,
    FaTextHeight,
    FaListUl,
    FaRegCalendarAlt,
    FaCheckCircle,
    FaFileUpload,
    FaStar,
    FaGripLines,
    FaHashtag, 
    FaPhone,
    FaPhoneAlt,
    FaEnvelope,
    FaImage,
    FaMobile
} from "react-icons/fa";
import { Droppable, Draggable } from "@hello-pangea/dnd";

const elementGroups = [
    {
        heading: "Layout",
        elements: [
            { id: "form-header", label: "Form Header", icon: <FaGripLines /> },
            { id: "image", label: "Image", icon: <FaImage /> }, // <-- Add this line
            // ...other elements
        ],
    },
    {
        heading: "Sections",
        elements: [
            { id: "section", label: "Section", icon: <FaListUl /> },
        ],
    },
    {
        heading: "Text Elements",
        elements: [
            { id: "short-answer", label: "Short Answer", icon: <FaFont /> },
            { id: "long-answer", label: "Paragraph", icon: <FaTextHeight /> },
            { id: "number", label: "Number Input", icon: <FaHashtag /> },
        ],
    },
    {
        heading: "Contact Elements",
        elements: [
            { id: "mobile", label: "Mobile Number", icon: <FaMobile /> },
            { id: "phone", label: "Phone Number", icon: <FaPhoneAlt /> },
            { id: "email", label: "Email Address", icon: <FaEnvelope /> },
        ],
    },
    {
        heading: "Choice Elements",
        elements: [
            { id: "radio", label: "Radio Button", icon: <FaCheckCircle /> },
            { id: "checkboxes", label: "Checkboxes", icon: <FaCheckCircle /> },
            { id: "dropdown", label: "Dropdown", icon: <FaListUl /> },
        ],
    },
    {
        heading: "Date/Time and File Upload",
        elements: [
            { id: "date", label: "Date", icon: <FaRegCalendarAlt /> },
            { id: "date-time", label: "Date & Time", icon: <FaRegCalendarAlt /> },
            { id: "file-upload", label: "File Upload", icon: <FaFileUpload /> },
        ],
    },
    {
        heading: "Rating and Scale",
        elements: [
            { id: "star-rating", label: "Star Rating", icon: <FaStar /> },
            { id: "linear-scale", label: "Linear Scale", icon: <FaGripLines /> },
        ],
    },
    {
        heading: "Advanced Elements",
        elements: [
            { id: "secret_token", label: "Secret Token", icon: <FaHashtag />, futureRelease: "Coming soon..." },
            { id: "user_password", label: "User Password", icon: <FaHashtag />, futureRelease: "Coming soon..." },
            { id: "enable_notification", label: "Enable Notification", icon: <FaCheckCircle />, futureRelease: "Coming soon..." },
            { id: "e_signature", label: "E-Signature", icon: <FaRegCalendarAlt />, futureRelease: "Coming soon..." },
            { id: "grid", label: "Matrix Grid", icon: <FaGripLines />, futureRelease: "Coming soon..." },
            { id: "repeat_group", label: "Repeat Group", icon: <FaListUl />, futureRelease: "Coming soon..." },
        ],
    },
];

// Export a constant for sidebar droppableId
export const SIDEBAR_DROPPABLE_ID = "sidebar";

const Sidebar = ({ onAddField }) => {

    const [searchTerm, setSearchTerm] = React.useState("");

    return (
        <Droppable
            droppableId={SIDEBAR_DROPPABLE_ID}
            isDropDisabled={true}
            renderClone={(provided, snapshot, rubric) => {
                // Flatten the elementGroups to get a flat list of sidebar items
                const flatElements = elementGroups.flatMap(group => group.elements.filter(item => !item.futureRelease && item.label.toLowerCase().includes(searchTerm.toLowerCase())));
                const item = flatElements[rubric.source.index];
                return (
                    <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className="group relative flex flex-col items-center p-4 bg-gradient-to-br from-white to-gray-50/80 dark:from-gray-800/90 dark:to-gray-700/80 backdrop-blur-sm border border-gray-200/50 dark:border-gray-600/50 rounded-xl shadow-md cursor-pointer transform hover:scale-105 hover:-translate-y-1 overflow-hidden"
                    >
                        <span className="relative text-xl text-blue-600 dark:text-blue-400">
                            {item.icon}
                        </span>
                        <span className="relative text-xs font-medium text-gray-800 dark:text-gray-300 text-center mt-2">
                            {item.label}
                        </span>
                    </div>
                );
            }}
            direction="vertical"
        >
            {(provided) => (
                <div ref={provided.innerRef} {...provided.droppableProps}>
                    {/* Mobile and Tablet Bottom Sidebar */}
                    <div className="fixed bottom-0 left-0 w-full bg-gradient-to-t from-white via-white to-white/95 dark:from-gray-900 dark:via-gray-900 dark:to-gray-900/95 backdrop-blur-lg border-t border-gray-200/50 dark:border-gray-700/50 shadow-2xl z-10 block md:hidden">
                        <div className="p-4 bg-gradient-to-br from-white to-gray-50/80 dark:from-gray-900 dark:to-gray-800/80 backdrop-blur-md rounded-t-2xl shadow-inner">
                            <div className="flex items-center justify-center mb-4">
                                <div className="w-12 h-1 bg-gradient-to-r from-blue-400 to-purple-500 rounded-full opacity-60"></div>
                            </div>
                            
                            <h3 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-400 dark:to-purple-400 bg-clip-text text-transparent text-center mb-3">
                                Form Elements
                            </h3>

                            {/* Search Input */}
                            <div className="relative mb-4">
                                <input
                                    type="text"
                                    placeholder="Search elements..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full px-4 py-3 text-sm bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border border-gray-200/50 dark:border-gray-600/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 dark:text-white placeholder-gray-400 shadow-lg transition-all duration-200 hover:shadow-xl"
                                />
                                <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-blue-500/10 to-purple-500/10 pointer-events-none"></div>
                            </div>

                            <div className="flex overflow-x-auto space-x-3 py-2 scrollbar-thin scrollbar-thumb-gray-300/50 scrollbar-track-transparent">
                                {elementGroups.flatMap((group) =>
                                group.elements
                                    .filter((item) =>
                                        item.label.toLowerCase().includes(searchTerm.toLowerCase())
                                    )
                                    .map((item, idx) => (
                                        item.futureRelease ? (
                                            <button
                                                key={item.id}
                                                disabled
                                                className="group relative w-28 h-28 flex-shrink-0 flex flex-col items-center justify-center p-3 bg-gradient-to-br from-gray-100 to-gray-50/80 dark:from-gray-800/90 dark:to-gray-700/80 backdrop-blur-sm border border-gray-200/50 dark:border-gray-600/50 rounded-xl shadow-md opacity-60 cursor-not-allowed"
                                                title={item.futureRelease}
                                            >
                                                <span className="relative text-xl text-gray-400 dark:text-gray-600">{item.icon}</span>
                                                <span className="relative text-xs font-medium text-gray-400 dark:text-gray-500 text-center mt-2 leading-tight">
                                                    {item.label}
                                                </span>
                                                <span className="relative text-[10px] font-medium text-red-500 mt-1">{item.futureRelease}</span>
                                            </button>
                                        ) : (
                                            <div
                                                key={item.id}
                                                onClick={() => onAddField(item.id)}
                                                className="group relative w-28 h-28 flex-shrink-0 flex flex-col items-center justify-center p-3 bg-gradient-to-br from-white to-gray-50/90 dark:from-gray-800 dark:to-gray-700/90 backdrop-blur-sm border border-gray-200/50 dark:border-gray-600/50 rounded-2xl shadow-lg hover:shadow-2xl hover:from-blue-50 hover:to-purple-50 dark:hover:from-gray-700 dark:hover:to-gray-600 transition-all duration-300 transform hover:scale-105 hover:-translate-y-1 cursor-pointer overflow-hidden"
                                            >
                                                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                                <span className="relative text-2xl text-blue-500 dark:text-blue-400 group-hover:text-blue-600 dark:group-hover:text-blue-300 transition-colors duration-200 transform group-hover:scale-110">
                                                    {item.icon}
                                                </span>
                                                <span className="relative text-xs font-medium text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white text-center mt-2 transition-colors duration-200">
                                                    {item.label}
                                                </span>
                                            </div>
                                        )
                                    ))
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Desktop Left Sidebar */}
                    <div className="hidden md:block w-64 h-screen bg-gradient-to-br from-white via-gray-50/50 to-white dark:from-gray-900 dark:via-gray-800/50 dark:to-gray-900 backdrop-blur-xl border-r border-gray-200/50 dark:border-gray-700/50 shadow-2xl">
                        <div className="flex flex-col justify-between h-full p-4">
                            <div className="text-center mb-6">
                                <h3 className="text-2xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 dark:from-blue-400 dark:via-purple-400 dark:to-indigo-400 bg-clip-text text-transparent tracking-wide">
                                    Form Elements
                                </h3>
                                <div className="w-16 h-1 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full mx-auto mt-2 opacity-60"></div>
                            </div>

                            {/* Search Input */}
                            <div className="relative mb-6">
                                <input
                                    type="text"
                                    placeholder="Search elements..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full px-4 py-3 text-sm bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border border-gray-200/50 dark:border-gray-600/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 dark:text-white placeholder-gray-400 shadow-lg transition-all duration-200 hover:shadow-xl"
                                />
                                <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-blue-500/5 to-purple-500/5 pointer-events-none"></div>
                            </div>

                            <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300/50 scrollbar-track-transparent lg:max-h-[72vh] max-h-[72vh] pr-2 mb-12">
                                {elementGroups.map((group, groupIndex) => {
                                    const filteredElements = group.elements.filter((item) =>
                                        item.label.toLowerCase().includes(searchTerm.toLowerCase())
                                    );

                                    // Skip group if no elements match the search
                                    if (filteredElements.length === 0) return null;

                                    return (
                                        <div key={groupIndex} className="mb-8">
                                            <div className="relative mb-4">
                                                <h4 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    {group.heading}
                                                </h4>
                                                <div className="absolute -bottom-1 left-0 w-8 h-0.5 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"></div>
                                            </div>
                                            
                                            <div className="grid grid-cols-2 gap-3 px-2">
                                                {filteredElements.map((item, idx) => (
                                                    item.futureRelease ? (
                                                        <button
                                                            key={item.id}
                                                            disabled
                                                            className="group relative flex flex-col items-center p-4 bg-gradient-to-br from-gray-100 to-gray-50/80 dark:from-gray-800/90 dark:to-gray-700/80 backdrop-blur-sm border border-gray-200/50 dark:border-gray-600/50 rounded-xl shadow-md opacity-60 cursor-not-allowed"
                                                            title={item.futureRelease}
                                                        >
                                                            <span className="relative text-xl text-gray-400 dark:text-gray-600">{item.icon}</span>
                                                            <span className="relative text-xs font-medium text-gray-400 dark:text-gray-500 text-center mt-2 leading-tight">
                                                                {item.label}
                                                            </span>
                                                            <span className="relative text-[10px] font-medium text-red-500 mt-1">{item.futureRelease}</span>
                                                        </button>
                                                    ) : (
                                                        <Draggable
                                                            key={item.id}
                                                            draggableId={`sidebar-${item.id}`}
                                                            index={idx}
                                                        >
                                                            {(provided) => (
                                                                <div
                                                                    ref={provided.innerRef}
                                                                    {...provided.draggableProps}
                                                                    {...provided.dragHandleProps}
                                                                    onClick={() => onAddField(item.id)}
                                                                    className="group relative flex flex-col items-center p-4 bg-gradient-to-br from-white to-gray-50/80 dark:from-gray-800/90 dark:to-gray-700/80 backdrop-blur-sm border border-gray-200/50 dark:border-gray-600/50 rounded-xl shadow-md hover:shadow-2xl hover:from-blue-50 hover:to-purple-50 dark:hover:from-gray-700 dark:hover:to-gray-600 transition-all duration-300 cursor-pointer transform hover:scale-105 hover:-translate-y-1 overflow-hidden"
                                                                >
                                                                    <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                                                    <span className="relative text-xl text-blue-600 dark:text-blue-400 group-hover:text-blue-700 dark:group-hover:text-blue-300 transition-all duration-200 transform group-hover:scale-110">
                                                                        {item.icon}
                                                                    </span>
                                                                    <span className="relative text-xs font-medium text-gray-800 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white text-center mt-2 transition-colors duration-200 leading-tight">
                                                                        {item.label}
                                                                    </span>
                                                                </div>
                                                            )}
                                                        </Draggable>
                                                    )
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                    {provided.placeholder}
                </div>
            )}
        </Droppable>
    );
};

export default Sidebar;